import { auth, ensureFirebase } from '../lib/firebase';
import { api } from '../lib/api';
import { API_BASE } from '../lib/api-base';

export interface ChatMessage {
  role: 'user' | 'model';
  content: string;
  createdAt?: string;
}

export const AVAILABLE_MODELS = [
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'gemini-3-flash', label: 'Gemini 3 Flash' },
];

export async function streamChat(
  message: string,
  history: ChatMessage[],
  model: string,
  onChunk: (text: string) => void,
  onStatus: (status: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
): Promise<void> {
  await ensureFirebase();
  const token = await auth.currentUser?.getIdToken();

  const res = await fetch(`${API_BASE}/api/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, history, model }),
  });

  if (!res.ok) {
    const text = await res.text();
    onError(text || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    onError('No response body');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.error) {
          onError(data.error);
          return;
        }
        if (data.done) {
          onDone();
          return;
        }
        if (data.status) {
          onStatus(data.status);
        }
        if (data.chunk) {
          onChunk(data.chunk);
        }
      } catch {
        // skip malformed lines
      }
    }
  }

  onDone();
}

export async function getChatHistory(locationId = '0'): Promise<ChatMessage[]> {
  return api<ChatMessage[]>(`/api/ai/history?location_id=${encodeURIComponent(locationId)}`);
}

export async function saveChatMessages(messages: ChatMessage[], locationId = '0'): Promise<void> {
  await api('/api/ai/history', {
    method: 'POST',
    body: JSON.stringify({ messages, locationId }),
  });
}

export async function clearChatHistory(locationId = '0'): Promise<void> {
  await api(`/api/ai/history?location_id=${encodeURIComponent(locationId)}`, {
    method: 'DELETE',
  });
}
